'use client'

import {
  CLIENT_METRICS,
  isClientMetricsEnabled,
  onClientMetricsDrain,
  recordClientMetric,
} from '@brew/observability/metrics-browser'
import { incrementTelemetryHealth } from '@brew/observability/telemetry-health'

const MEASURES = new Set([
  'email-stream-queue',
  'email-stream-parse',
  'email-stream-prepare',
  'email-stream-apply',
  'email-stream-promotion',
  'email-stream-receive-to-document',
  'email-parse-complete',
  'email-parse-streaming',
  'email-editor-guard-apply',
  'email-editor-element-ai',
  'email-viewer-update',
  'canvas-layout',
  'canvas-scene-upload',
  'canvas-camera-write',
  'canvas-hud-update',
])
const WINDOW_MS = 60_000
const MAX_ENTRIES_PER_CALLBACK = 128
const MAX_WINDOW_GROUPS = 96

function readDetail(detail: unknown, key: string): unknown {
  try {
    return detail && typeof detail === 'object'
      ? Reflect.get(detail, key)
      : undefined
  } catch {
    return undefined
  }
}

/**
 * Export content-free Performance measures in fixed-size summaries. No per-
 * chunk RUM actions, new frame loop, entry detail, or unbounded retained history.
 * Window sums/maxima are explicitly window statistics, never raw percentiles.
 */
export function installBrowserPerformanceMetrics(): () => void {
  if (!isClientMetricsEnabled() || typeof PerformanceObserver === 'undefined') {
    return () => {}
  }
  const windows = new Map<
    string,
    {
      phase: string
      engine?: string
      outcome?: string
      count: number
      sum: number
      max: number
    }
  >()
  let timer: ReturnType<typeof setTimeout> | undefined
  let dropped = 0
  let observer: PerformanceObserver | undefined
  const flush = () => {
    // A final measure may still be queued when unmount/pagehide runs.
    // takeRecords removes it from the observer queue, preventing duplicate delivery.
    try {
      if (observer) {
        collectEntries(observer.takeRecords())
      }
    } catch {
      incrementTelemetryHealth('client_lifecycle_failed')
    }
    clearTimeout(timer)
    timer = undefined
    for (const summary of windows.values()) {
      const tags = {
        domain: 'rendering',
        surface: summary.phase.startsWith('canvas-') ? 'canvas' : 'email',
        phase: summary.phase,
        ...(summary.engine ? { engine: summary.engine } : {}),
        ...(summary.outcome ? { outcome: summary.outcome } : {}),
        sample_policy: 'aggregate_60s',
      }
      recordClientMetric(CLIENT_METRICS.renderCount, summary.count, { tags })
      recordClientMetric(CLIENT_METRICS.renderDuration, summary.sum, {
        tags: { ...tags, resource: 'sum' },
      })
      recordClientMetric(CLIENT_METRICS.renderDuration, summary.max, {
        tags: { ...tags, resource: 'max' },
      })
    }
    windows.clear()
    if (dropped > 0) {
      recordClientMetric(CLIENT_METRICS.renderCount, dropped, {
        tags: {
          domain: 'telemetry',
          surface: 'performance_observer',
          reason: 'callback_budget',
          outcome: 'skipped',
        },
      })
      dropped = 0
    }
  }
  const collect = (
    phase: string,
    duration: number,
    engine?: string,
    outcome?: string
  ) => {
    const key = `${phase}:${engine ?? ''}:${outcome ?? ''}`
    let summary = windows.get(key)
    if (!summary) {
      if (windows.size >= MAX_WINDOW_GROUPS) {
        dropped += 1
        incrementTelemetryHealth('collector_buffer_overflow')
        return
      }
      summary = { phase, engine, outcome, count: 0, sum: 0, max: 0 }
      windows.set(key, summary)
    }
    summary.count += 1
    summary.sum += duration
    summary.max = Math.max(summary.max, duration)
  }
  function collectEntries(entries: Array<PerformanceEntry>): void {
    const overflow = Math.max(0, entries.length - MAX_ENTRIES_PER_CALLBACK)
    dropped += overflow
    incrementTelemetryHealth('collector_buffer_overflow', overflow)
    for (
      let index = 0;
      index < Math.min(entries.length, MAX_ENTRIES_PER_CALLBACK);
      index += 1
    ) {
      const entry = entries[index]
      if (
        !MEASURES.has(entry.name) ||
        !Number.isFinite(entry.duration) ||
        entry.duration < 0
      ) {
        continue
      }
      const detail: unknown = 'detail' in entry ? entry.detail : undefined
      const backend = readDetail(detail, 'backend')
      const engine =
        backend === 'worker' ||
        backend === 'main' ||
        backend === 'style' ||
        backend === 'waapi'
          ? backend
          : undefined
      const rawOutcome = readDetail(detail, 'outcome')
      const outcome =
        rawOutcome === 'success' ||
        rawOutcome === 'failure' ||
        rawOutcome === 'partial' ||
        rawOutcome === 'skipped'
          ? rawOutcome
          : undefined
      collect(entry.name, entry.duration, engine, outcome)
      // Worker CPU belongs to the worker clock; renderer round-trip includes queue/transport.
      const cpu = readDetail(detail, 'parseMs')
      if (
        entry.name === 'email-stream-parse' &&
        typeof cpu === 'number' &&
        Number.isFinite(cpu) &&
        cpu >= 0 &&
        cpu <= 1e15
      ) {
        collect('email-parser-cpu', cpu, engine, outcome)
      }
    }
    if (timer === undefined && (windows.size > 0 || dropped > 0)) {
      timer = setTimeout(flush, WINDOW_MS)
    }
  }
  try {
    observer = new PerformanceObserver((list) =>
      collectEntries(list.getEntries())
    )
    observer.observe({ type: 'measure' })
  } catch {
    incrementTelemetryHealth('client_lifecycle_failed')
    return () => {}
  }
  const removeDrain = onClientMetricsDrain(flush)
  window.addEventListener('pagehide', flush)
  return () => {
    flush()
    try {
      observer?.disconnect()
    } catch {
      incrementTelemetryHealth('client_lifecycle_failed')
    }
    window.removeEventListener('pagehide', flush)
    removeDrain()
  }
}
